import { useCallback, useEffect, useState } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import useUserStore from "@store/useUserStore";
import SubHeader from "components/common/SubHeader";
import LoadingSpinner from "components/common/LoadingSpiner";
import NoData from "components/common/NoData";
import { useSnackbar } from "contexts/SnackbarContext";
import {
    cancelLessonRequest,
    fetchMyLessonRequests,
    getLessonRequestErrorMessage,
} from "api/lessonRequest";
import {
    LESSON_REQUEST_STATUS,
    LESSON_REQUEST_STATUS_LABEL,
    normalizeLessonRequestList,
} from "constants/lessonRequest";
import LessonRequestCard from "../../components/lessonRequest/LessonRequestCard";
import "./LessonRequest.scss";

const STATUS_TABS = [
    LESSON_REQUEST_STATUS.ALL,
    LESSON_REQUEST_STATUS.PENDING,
    LESSON_REQUEST_STATUS.CONFIRMED,
    LESSON_REQUEST_STATUS.OPENED,
    LESSON_REQUEST_STATUS.REJECTED,
    LESSON_REQUEST_STATUS.EXPIRED,
    LESSON_REQUEST_STATUS.CANCELLED,
];

const normalizeStatusParam = (status) => {
    const normalized = String(status || "").toUpperCase();
    return STATUS_TABS.includes(normalized) ? normalized : LESSON_REQUEST_STATUS.PENDING;
};

export default function MyLessonRequests() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const userInfo = useUserStore((state) => state.userInfo);
    const { showSuccess, showError } = useSnackbar();
    const [activeStatus, setActiveStatus] = useState(() => normalizeStatusParam(searchParams.get("status")));
    const [requests, setRequests] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState(null);

    useEffect(() => {
        const nextStatus = normalizeStatusParam(searchParams.get("status"));
        setActiveStatus((current) => (current === nextStatus ? current : nextStatus));
    }, [searchParams]);

    const handleStatusChange = (status) => {
        setActiveStatus(status);
        if (status === LESSON_REQUEST_STATUS.PENDING) {
            setSearchParams({});
        } else {
            setSearchParams({ status });
        }
    };

    const loadRequests = useCallback(async () => {
        if (!userInfo) return;
        setIsLoading(true);
        setErrorMessage(null);
        try {
            const data = await fetchMyLessonRequests(activeStatus);
            setRequests(normalizeLessonRequestList(data));
        } catch (error) {
            setErrorMessage(getLessonRequestErrorMessage(error));
        } finally {
            setIsLoading(false);
        }
    }, [activeStatus, userInfo]);

    useEffect(() => {
        loadRequests();
    }, [loadRequests]);

    const handleCancel = async (id) => {
        if (!window.confirm("대기중인 수업 요청을 취소할까요?")) return;
        try {
            await cancelLessonRequest(id);
            showSuccess("수업 요청을 취소했어요.");
            loadRequests();
        } catch (error) {
            showError(getLessonRequestErrorMessage(error));
        }
    };

    if (!userInfo) {
        return <Navigate to="/login" replace />;
    }

    return (
        <div className="lesson-request-page lesson-request-page--list">
            <SubHeader title="내 수업 요청" />
            <nav className="lesson-request-tabs" aria-label="수업 요청 상태 필터">
                {STATUS_TABS.map((status) => (
                    <button
                        type="button"
                        key={status}
                        className={activeStatus === status ? "is-active" : ""}
                        onClick={() => handleStatusChange(status)}
                    >
                        {LESSON_REQUEST_STATUS_LABEL[status]}
                    </button>
                ))}
            </nav>
            <main className="lesson-request-list">
                {isLoading && <LoadingSpinner />}
                {!isLoading && errorMessage && (
                    <div className="lesson-request-state">
                        <NoData title="수업 요청을 불러오지 못했어요" desc={errorMessage} />
                        <button type="button" className="lesson-request-primary" onClick={loadRequests}>다시 불러오기</button>
                    </div>
                )}
                {!isLoading && !errorMessage && requests.length === 0 && (
                    <div className="lesson-request-state">
                        <NoData title="수업 요청이 없어요" desc="원하는 날짜와 시간으로 강사에게 수업 개설을 요청해 보세요." />
                    </div>
                )}
                {!isLoading && !errorMessage && requests.map((request) => (
                    <LessonRequestCard
                        key={request.id}
                        request={request}
                        onCancel={handleCancel}
                        onOpen={(id) => navigate(`/lesson-requests/${id}`)}
                    />
                ))}
            </main>
            <div className="btn-wrap btn-bottom-fixed">
                <button type="button" className="btn btn-full btn-purple500 btn-large" onClick={() => navigate("/lesson-requests/new")}>
                    수업 요청하기
                </button>
            </div>
        </div>
    );
}
